import { useRef } from 'react';
import debounce from 'lodash/debounce';
import each from 'lodash/each';
import isBoolean from 'lodash/isBoolean';
import omit from 'lodash/omit';
import camelCase from 'lodash/camelCase';
import orderBy from 'lodash/orderBy';
import capitalize from 'lodash/capitalize';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';
import { useEffect, useState } from 'react';
import { apiGET, apiPOST } from './networking';
import { useDispatch, useSelector } from 'react-redux';
import { setActiveRecommendation } from '../store/actions/recommendationActions';
import { selectSliceRecommendationItem } from '../store/reducers/recommendationReducer';
import { EEvaluationStatus, ESortDirection, selectSliceDashboard } from '../store/reducers/dashboardReducer';
import { setDashboardData } from '../store/actions/dashboardActions';
import { DEV_MODE } from './Constants';

const RECOMMENDATION_DEFAULT_NAME = 'Evaluation';
const MAX_MEMBERS_COUNT = 9;
export const RECOMMENDATION_STORE_DATA_SET_DEBOUCE_TIME = 200;

/**
 * @typedef {'SaveDraft'|'SaveDraftAndReturn'|'ObservationHoursSubmit'|'PreviewCommitteeAndSubmit'|'PreviewAndSubmit'} TFormSubmittionState
 */

export const FORM_SUBMISSION_MODE = /** @type {Record<TFormSubmittionState, TFormSubmittionState>} */ ({
  SaveDraft: 'SaveDraft',
  SaveDraftAndReturn: 'SaveDraftAndReturn',
  ObservationHoursSubmit: 'ObservationHoursSubmit',
  PreviewCommitteeAndSubmit: 'PreviewCommitteeAndSubmit',
  PreviewAndSubmit: 'PreviewAndSubmit',
});

const EVALUATION_REQUEST_STATUS = {
  COMPLETED: 'completed',
  REQUESTED: 'requested',
  ACCEPTED: 'accepted',
  DECLINED: 'declined',
  EXPIRED: 'expired',
};

/**
 *
 * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} recommendationRequest
 */
export const getEvaluationRequestUtils = recommendationRequest => {
  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {boolean}
   */
  const isCompleted = request => {
    return request?.status === EVALUATION_REQUEST_STATUS.COMPLETED;
  };

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {boolean}
   */
  const isRequested = request => {
    return request?.status === EVALUATION_REQUEST_STATUS.REQUESTED;
  };

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {boolean}
   */
  const isAccepted = request => {
    return request?.status === EVALUATION_REQUEST_STATUS.ACCEPTED;
  };

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {boolean}
   */
  const isCompletedAndHasParent = request => {
    return isCompleted(request) && request?.copiedFromEvaluation !== 0;
  };

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {boolean}
   */
  const isCompletedAndNoParent = request => {
    return isCompleted(request) && request?.copiedFromEvaluation === 0;
  };

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @param {number=} templateId
   * @returns {boolean}
   */
  const isAutoFilledBy = (request, templateId) => {
    return !!(isCompleted(request) && templateId && request?.copiedFromEvaluation === templateId);
  };

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {boolean}
   */
  const isReUseForFutureEvalsEnable = request => {
    return isCompletedAndNoParent(request) && request?.reUseForFutureEvals !== null;
  };
  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {string}
   */
  const getGroupKey = request => {
    return [request?.instanceId, request?.casId, request?.evaluationType].join(':');
  };

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {string}
   */
  const getApplicantFullName = request => {
    return request?.applicantFirstName ? `${request.applicantFirstName} ${request.applicantLastName}` : '';
  };

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   */
  const getEvaluationDisplayName = request => {
    const result = request?.evaluationsDisplayName ? request.evaluationsDisplayName : RECOMMENDATION_DEFAULT_NAME;
    class MyString extends String {
      toSingleForm = () => result?.replace(/s$/, '') ?? '';
      toPluralForm = () => `${result}s`;
      valueOf = () => result;
    }
    return new MyString(result);
  };

  /**
   * This declines any evaluation request with the message
   * @param {string} mesaage
   * @returns {Promise<{isDeclined:Boolean, isDeleted?:Boolean, }>}
   */
  const decline = async mesaage => {
    if (!recommendationRequest) {
      return { isDeclined: false };
    }
    try {
      await apiPOST('/recommendation/rest/declineRecommendation/' + recommendationRequest.id, mesaage);
      return { isDeclined: true };
    } catch (error) {
      return { isDeclined: false, isDeleted: /404/.test(error.status) };
    }
  };

  /**
   * This reopens any evaluation request
   * @returns {Promise<{ isReopened?:Boolean, }>}
   */
  const reopen = async () => {
    if (!recommendationRequest) {
      return { isReopened: false };
    }
    try {
      const result = await apiPOST('/recommendation/rest/reopenEvaluation/' + recommendationRequest.id);
      return { isReopened: result.body?.toString() === 'true' };
    } catch (error) {
      return { isReopened: false };
    }
  };

  // recommendationRequest
  return {
    recommendationRequest,
    linkPDF: () => window.location.origin + '/recommendation/rest/viewRecommendation/' + recommendationRequest?.id,
    getNote: () => recommendationRequest?.message,
    isCommittee: () => recommendationRequest?.evaluationType === 'Committee',
    isObservation: () => recommendationRequest?.evaluationType === 'Observation Hours',
    isWaived: () => !!recommendationRequest?.waiveRightOfAccess,
    getEvaluationDisplayName: () => getEvaluationDisplayName(recommendationRequest),
    getApplicantFullName: () => getApplicantFullName(recommendationRequest),
    getApplicantEmail: () => recommendationRequest?.applicantEmail,
    getGroupKey: () => getGroupKey(recommendationRequest),
    isReUseForFutureEvalsEnable: () => isReUseForFutureEvalsEnable(recommendationRequest),
    isAccepted: () => isAccepted(recommendationRequest),
    isCompleted: () => isCompleted(recommendationRequest),
    isAutoFilledBy: (/** @type {number=} */ templateId) => isAutoFilledBy(recommendationRequest, templateId),
    isRequested: () => isRequested(recommendationRequest),
    isCompletedAndHasParent: () => isCompletedAndHasParent(recommendationRequest),
    isCompletedAndNoParent: () => isCompletedAndNoParent(recommendationRequest),
    delegation: {
      delegated: recommendationRequest?.delegated,
      delegationDisabled: recommendationRequest?.delegationDisabled,
      completedBy: recommendationRequest?.completedBy,
    },
    actions: {
      decline,
      reopen,
    },
  };
};

/**
 *
 * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest[]} recommendationRequests
 */
const getEvaluationRequestsUtils = recommendationRequests => {
  /** @typedef {Record<string, { requests:import('../store/reducers/dashboardReducer').TEvaluationRequest[]; reusedRequestId?:number|null }>} TGroupEvaluationRequest */

  /**
   * grab all linked requests for the given request when it is not copied from another
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest} parentRequest
   */
  const getAutoFilledRequestsByTemplateId = parentRequest => {
    return getEvaluationRequestUtils(parentRequest).isCompletedAndNoParent()
      ? recommendationRequests?.filter(request => {
          return getEvaluationRequestUtils(request).isAutoFilledBy(parentRequest.id);
        }) ?? []
      : [];
  };

  /**
   *
   * @param {TGroupEvaluationRequest[number]} groupInfoSlice
   */
  const hasReusedRequestInSameGroup = groupInfoSlice => {
    const result =
      groupInfoSlice?.requests?.filter(
        request =>
          getEvaluationRequestUtils(request).isCompletedAndNoParent() && request.id === groupInfoSlice.reusedRequestId
      ).length > 0;
    return result;
  };

  const getDisplayRequests = () => {
    return recommendationRequests ?? [];
  };

  const getListDisplayRequests = () => {
    const reqItems = getDisplayRequests().filter(item => {
      // exclude completed and with parent evaluation
      return !getEvaluationRequestUtils(item).isCompletedAndHasParent();
    });
    return reqItems;
  };

  const getGroupCompletedRequests = () => {
    // group completed evaluations
    const groupedRequests = getDisplayRequests()
      .filter(request => {
        return getEvaluationRequestUtils(request).isCompleted();
      })
      .reduce((group, request) => {
        const groupKey = getEvaluationRequestUtils(request).getGroupKey();
        group[groupKey] = group[groupKey] || { requests: [] };
        group[groupKey].requests.push(request);
        if (getEvaluationRequestUtils(request).isCompletedAndNoParent()) {
          group[groupKey].reusedRequestId =
            group[groupKey].reusedRequestId || (request.reUseForFutureEvals ? request.id : null);
        }
        return group;
      }, /** @type {TGroupEvaluationRequest} */ ({}));
    return groupedRequests;
  };

  return {
    getListDisplayRequests,
    getGroupCompletedRequests,
    hasReusedRequestInSameGroup,
    getAutoFilledRequestsByTemplateId,
    evaluations: recommendationRequests.map(getEvaluationRequestUtils),
  };
};

/**
 *
 * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} recommendationRequestInput
 */
export const useEvaluationRequestUtils = recommendationRequestInput => {
  const [recommendationRequest, setRecommendationRequest] = useState(recommendationRequestInput);
  const [declining, setDeclining] = useState(false);
  const [reopening, setReopening] = useState(false);
  const result = getEvaluationRequestUtils(recommendationRequest);

  useEffect(() => {
    setRecommendationRequest(recommendationRequestInput);
  }, [recommendationRequestInput]);

  return {
    ...result,
    busyState: { declining },
    actions: {
      ...result.actions,
      decline: async (/** @type {string} mesaage */ message) => {
        setDeclining(true);
        const opRes = await result.actions.decline(message);
        setDeclining(false);
        return opRes;
      },
      reopen: async () => {
        setReopening(true);
        const opRes = await result.actions.reopen();
        setReopening(false);
        return opRes;
      },
    },
  };
};
/**
 *
 * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest[]} recommendationRequests
 */
export const useEvaluationRequestsUtils = recommendationRequests => {
  const result = getEvaluationRequestsUtils(recommendationRequests);
  // const evaluations = recommendationRequests.map(useEvaluationRequestUtils);

  return {
    ...result,
    // evaluations,
  };
};

export const useDashboard = () => {
  const dispatch = useDispatch();
  const storeDashboard = useSelector(selectSliceDashboard);
  const [loading, setLoading] = useState(true);
  const [dashboardData, _setDashboardData] = useState(
    /** @type {?import('../store/reducers/dashboardReducer').TDataDashboard=} */ (storeDashboard.dashboardData)
  );

  const [displayEvaluationRequests, setDisplayEvaluationRequests] = useState(
    /** @type {import('../store/reducers/dashboardReducer').TEvaluationRequest[]} */ ([])
  );

  const [filterByStatus, setFilterByStatus] = useState(
    storeDashboard.viewParams?.filterByStatus ?? [EEvaluationStatus.accepted, EEvaluationStatus.requested]
  ); //[EEvaluationStatus.accepted, EEvaluationStatus.requested]
  const [sortDirection, applySortDirection] = useState(
    storeDashboard.viewParams?.sortDirection ?? ESortDirection.Ascending
  ); //ESortDirection.Ascending
  const [selectedSort, applySortBy] = useState(storeDashboard.viewParams?.selectedSort ?? 'requestedOn'); //'requestedOn'
  const [allExpanded, setAllExpanded] = useState(!!storeDashboard.viewParams?.allExpanded); //false

  const utils = useEvaluationRequestsUtils(displayEvaluationRequests);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const resp = await apiGET('/recommendation/rest/dashboard');
      dispatch(setDashboardData(resp.body));
      _setDashboardData(resp.body);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // save state into the store

  /**
   *
   * @param {EEvaluationStatus[]} filterByStatus
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest[]=} requests
   */
  const extractDisplayRequests = (filterByStatus, requests) => {
    // let dashboardData = [];
    // if (filterByStatus?.length !== 0) {
    //   dashboardData = filter(requests || [], request => {
    //     return includes(filterByStatus, request.status);
    //   });
    // }
    return (
      requests
        ?.filter(r => filterByStatus.includes(r.status))
        .map(item => ({
          ...item,
          applicantFirstName: capitalize(item.applicantFirstName),
          applicantLastName: capitalize(item.applicantLastName),
        })) ?? []
    );
  };

  /**
   *
   * @param {ESortDirection} sortDirection
   * @param {string} sortBy
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest[]} list
   * @returns {import('../store/reducers/dashboardReducer').TEvaluationRequest[]}
   */
  const applySorts = (sortDirection = 'Ascending', sortBy = 'requestedOn', list = []) => {
    const sortDir = sortDirection === 'Ascending' || sortDirection === 'asc' ? 'asc' : 'desc';

    let sortType = 'requestedOnDate';
    if (sortBy === 'LastName' || sortBy === 'FirstName') {
      sortType = camelCase('applicant' + sortBy);
    } else if (sortBy === 'requestedOn' || sortBy === 'requestedBy') {
      sortType = camelCase(sortBy + 'Date');
    } else if (sortBy === 'Type') {
      sortType = camelCase('evaluation' + sortBy);
    }
    return orderBy(list, [sortType], [sortDir]);
  };

  // store state into redux
  useEffect(() => {
    dispatch({
      type: 'SET_DASHBOARD_VIEW_PARAMS',
      payload: {
        viewParams: {
          filterByStatus,
          sortDirection,
          selectedSort,
          allExpanded,
        },
      },
    });
  }, [storeDashboard, filterByStatus, sortDirection, selectedSort, allExpanded]);

  // monitor the stored dashboard
  useEffect(() => {
    const evaluationRequests = storeDashboard.dashboardData?.recommendationRequest;
    _setDashboardData(storeDashboard.dashboardData);
    setDisplayEvaluationRequests(
      applySorts(sortDirection, selectedSort, extractDisplayRequests(filterByStatus, evaluationRequests))
    );
  }, [storeDashboard, filterByStatus, sortDirection, selectedSort]);

  return {
    ...utils,
    busyState: {
      loading,
      // anyDeclining: utils.evaluations.some(eval => eval.),
    },
    allExpanded,
    statusCount: dashboardData?.statusCount,
    actions: {
      fetchDashboard,
      setFilterByStatus,
      applySortDirection,
      applySortBy,
      setAllExpanded,
    },
  };
};

export const useRecommendation = () => {
  const dispatch = useDispatch();
  const storedRecommendation = useSelector(selectSliceRecommendationItem);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingMode, setSavingMode] = useState(/** @type {TFormSubmittionState=} */ (void 0));
  const [completeResult, setCompleteResult] = useState(/** @type {Boolean=} */ (void 0));
  const [questionIdsToBlockIdsMap, setQuestionIdsToBlockIdsMap] = useState(
    /** @type {Record<number, number>=} */ (void 0)
  );
  const [recommendation, _updateRecommendation] = useState(
    /** @type {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} */ (storedRecommendation)
  );
  const evaluationRequest = useEvaluationRequestUtils(recommendation?.recommendationRequest);
  const [isFetched, setIsFetched] = useState(!!recommendation?.recommendationRequest);
  const [isChanged, setIsChanged] = useState(false);

  const storeEvaluationDebounced = useRef(
    debounce(
      lastValue => {
        dispatch(setActiveRecommendation(lastValue));
      },
      RECOMMENDATION_STORE_DATA_SET_DEBOUCE_TIME,
      { leading: false }
    )
  );

  const updateRecommendation = (
    /** @type {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} */ nextRecommendation,
    immediate = false
  ) => {
    _updateRecommendation(nextRecommendation);
    immediate
      ? dispatch(setActiveRecommendation(nextRecommendation))
      : storeEvaluationDebounced.current(nextRecommendation);
  };

  useEffect(() => {
    _updateRecommendation(storedRecommendation);

    const questionToBlocksMap = storedRecommendation?.evaluationFormSection?.evaluationQuestionBlocks.reduce(
      (resp, questionBlock) => {
        return {
          ...resp,
          ...questionBlock.evaluationQuestions.reduce(
            (innerResp, question) => ({
              ...innerResp,
              [question.id]: questionBlock.id,
            }),
            /** @type {Record<number, number>} */ ({})
          ),
        };
      },
      /** @type {Record<number, number>} */ ({})
    );

    setQuestionIdsToBlockIdsMap(questionToBlocksMap);

    // detect changes
    setIsChanged(JSON.stringify(storedRecommendation).includes('ui__changed":true'));
  }, [storedRecommendation]);

  /**
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} request
   * @param {number=} id
   */
  const getEvalationQuestionBlockById = (request, id) => {
    return request?.evaluationFormSection?.evaluationQuestionBlocks?.find(block => block.id === id);
  };

  /**
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} request
   * @param {number=} id
   */
  const getEvalationQuestionById = (request, id) => {
    return request?.evaluationFormSection?.evaluationQuestionBlocks
      ?.flatMap(qBlock => qBlock.evaluationQuestions)
      .filter(qItem => qItem.id === id)[0];
  };

  const nestedOmit = (/** @type {Object|Array} */ obj, /** @type {string[]} */ iteratee) => {
    if (obj === null || Number.isNaN(obj) || typeof obj !== 'object') {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map(val => nestedOmit(val, iteratee));
    } else {
      const r = omit(obj, iteratee);
      each(r, function (val, key) {
        r[key] = nestedOmit(val, iteratee);
      });
      return r;
    }
  };

  /**
   *
   * @param {import('../store/reducers/dashboardReducer').TEvaluationDetails} requestData
   * @returns {import('../store/reducers/dashboardReducer').TEvaluationDetails}
   */
  const removingUids = requestData => {
    const rrr = nestedOmit(requestData, ['@id']);
    return rrr;
  };

  const stripHtmlTags = (/** @type {?string=} */ htmlText) => {
    return htmlText?.replace(/(<([^>]+)>)/gi, '').trim() ?? '';
  };

  return {
    item: recommendation,
    busyState: {
      busy: [loading, saving, completing, evaluationRequest.busyState.declining].some(Boolean),
      loading,
      saving,
      completing,
      savingMode,
      savingToDraft: saving && savingMode === FORM_SUBMISSION_MODE.SaveDraft,
      savingToReturn: saving && savingMode === FORM_SUBMISSION_MODE.SaveDraftAndReturn,
      savingNonSubmit: saving && savingMode?.startsWith(FORM_SUBMISSION_MODE.SaveDraft),
      savingToSubmit: saving && !savingMode?.startsWith(FORM_SUBMISSION_MODE.SaveDraft),
      ...evaluationRequest.busyState,
    },
    aliasVars: {
      id: recommendation?.recommendationRequest.id ?? '',
      firstName: recommendation?.recommendationRequest.applicantFirstName ?? '',
      lastName: recommendation?.recommendationRequest.applicantLastName ?? '',
      message: recommendation?.recommendationRequest.message ?? '',
      questionBlocks: recommendation?.evaluationFormSection?.evaluationQuestionBlocks ?? [],
      instanceName: recommendation?.evaluationFormSection?.evaluationForm.applicationForm.instanceName ?? '',
      evaluationType: recommendation?.recommendationRequest.evaluationType ?? '',
      submit: recommendation?.submit,
      status: recommendation?.status,
      completed: completeResult,
      hasInstructions: stripHtmlTags(recommendation?.evaluationFormSection?.instructions).length > 0,
      ratings: {
        count: recommendation?.evaluationFormRubricsRatings.length ?? 0,
        active: recommendation?.evaluationFormSection?.evaluationQuestionBlocks.find(
          qBlock => qBlock.constantBlockName === 'Rubrics'
        ),
        allSetToNotObserved: recommendation?.evaluationFormSection?.evaluationQuestionBlocks
          .find(qBlock => qBlock.constantBlockName === 'Rubrics')
          ?.evaluationQuestions.every(question => question.userAnswer?.[0]?.answer === 'Not Observed'),
      },
      notFound: !recommendation && isFetched,
      isReady: !!recommendation && isFetched,
      isChanged,
    },

    // general fns
    evaluationRequest,
    actions: {
      // form-specific sections
      committeeMembers: {
        count: recommendation?.committeeMembers.length ?? 0,
        capacity: MAX_MEMBERS_COUNT - (recommendation?.committeeMembers.length ?? 0),
        allowAddMore: (recommendation?.committeeMembers.length ?? 0) < MAX_MEMBERS_COUNT,
        addNew() {
          const committeeMembers_ui__initial = [
            ...(recommendation?.committeeMembers_ui__initial ?? recommendation?.committeeMembers ?? []),
          ];
          recommendation
            ? updateRecommendation({
                ...recommendation,
                committeeMembers: [...recommendation.committeeMembers, { memberName: '' }],
                committeeMembers_ui__initial,
                committeeMembers_ui__changed: true,
              })
            : void 0;
        },
        /**
         * @param {string} memberName
         * @param {number} index
         */
        updateAt(memberName, index) {
          const committeeMembers_ui__initial = [
            ...(recommendation?.committeeMembers_ui__initial ?? recommendation?.committeeMembers ?? []),
          ];
          const committeeMembers =
            recommendation?.committeeMembers?.map((member, idx) =>
              idx === index ? { ...member, memberName } : member
            ) ?? [];
          const committeeMembers_ui__changed = !isEqual(
            map(committeeMembers, 'memberName'),
            map(committeeMembers_ui__initial, 'memberName')
          );
          recommendation
            ? updateRecommendation({
                ...recommendation,
                committeeMembers,
                committeeMembers_ui__initial,
                committeeMembers_ui__changed,
              })
            : void 0;
        },
        /**
         * @param {number} index
         */
        removeAt(index) {
          const committeeMembers_ui__initial = [
            ...(recommendation?.committeeMembers_ui__initial ?? recommendation?.committeeMembers ?? []),
          ];
          const committeeMembers = recommendation?.committeeMembers.filter((_, idx) => idx !== index) ?? [];
          const committeeMembers_ui__changed = !isEqual(
            map(committeeMembers, 'memberName'),
            map(committeeMembers_ui__initial, 'memberName')
          );
          recommendation
            ? updateRecommendation({
                ...recommendation,
                committeeMembers,
                committeeMembers_ui__initial,
                committeeMembers_ui__changed,
              })
            : void 0;
        },
      },
      observationHours: {
        /**
         * @param {boolean} checkApprove
         */
        approveState(checkApprove) {
          recommendation
            ? updateRecommendation({
                ...recommendation,
                observationHoursDataEval: {
                  ...recommendation.observationHoursDataEval,
                  checkApprove,
                },
                observationHoursDataEval_ui__initial: {
                  ...(recommendation.observationHoursDataEval_ui__initial ?? recommendation.observationHoursDataEval),
                },
                observationHoursDataEval_ui__changed: !isEqual(
                  {
                    ...recommendation.observationHoursDataEval,
                    checkApprove,
                  },
                  recommendation.observationHoursDataEval_ui__initial ?? recommendation.observationHoursDataEval
                ),
              })
            : void 0;
        },
        handleChange(/** @type {string} */ propertyName, /** @type {any} */ value) {
          recommendation
            ? updateRecommendation({
                ...recommendation,
                observationHoursDataEval: {
                  ...recommendation.observationHoursDataEval,
                  [propertyName]: value,
                },
                observationHoursDataEval_ui__initial: {
                  ...(recommendation.observationHoursDataEval_ui__initial ?? recommendation.observationHoursDataEval),
                },
                observationHoursDataEval_ui__changed: !isEqual(
                  {
                    ...recommendation.observationHoursDataEval,
                    [propertyName]: value,
                  },
                  recommendation.observationHoursDataEval_ui__initial ?? recommendation.observationHoursDataEval
                ),
              })
            : void 0;
        },
      },
      async fetch(/** @type {?string=} */ id) {
        setLoading(true);
        updateRecommendation(null, true);

        try {
          const response = id ? await apiGET('/recommendation/rest/recommendationForm/' + id) : null;
          const item = response ? removingUids(response.body) : null;

          if (!item) {
            throw new Error('Cannot fetch evaluation form by id:' + id);
          }
          updateRecommendation(item);
          return item;
        } catch (error) {
          error ? console.error(error) : void 0;
        } finally {
          setIsFetched(true);
          setLoading(false);
        }
      },
      /**
       * This saves evaluation form, or submits observation hours
       * @param {TFormSubmittionState} mode
       * @param {?{ (arg0: boolean, arg1: import('../store/reducers/dashboardReducer').TEvaluationDetails): Promise<void>; }=} postActionHook
       * @returns {Promise<{ isSaved: boolean, mode:TFormSubmittionState, status?:'COMPLETE'|'INCOMPLETE', isDeleted?: boolean, }>}
       */
      async save(mode, postActionHook) {
        if (!recommendation) {
          return { isSaved: false, mode };
        }
        const info = getEvaluationRequestUtils(recommendation.recommendationRequest);

        const isDraft = mode.startsWith(FORM_SUBMISSION_MODE.SaveDraft);
        // in case it is an ObservationHours evaluation form - save it as completed form;
        const nextSubmitStatus = info.isObservation() && !isDraft ? 'COMPLETE' : 'INCOMPLETE';

        setSaving(true);
        setSavingMode(mode);
        setIsChanged(false);
        try {
          const payload = nestedOmit(recommendation, [
            'committeeMembers_ui__changed',
            'committeeMembers_ui__initial',
            'observationHoursDataEval_ui__changed',
            'observationHoursDataEval_ui__initial',
            'userAnswer_ui__changed',
            'userAnswer_ui__initial',
            'id_ui__changed',
            'id_ui__initial',
          ]);

          await apiPOST(`/recommendation/rest/recommendationForm`, {
            ...payload,
            submit: nextSubmitStatus,
          });

          // here we handle 2xx status response

          updateRecommendation({
            ...payload,
            submit: nextSubmitStatus,
          });

          await new Promise(r => setTimeout(r, RECOMMENDATION_STORE_DATA_SET_DEBOUCE_TIME));

          await postActionHook?.(true, {
            ...payload,
            submit: nextSubmitStatus,
          });

          // custom scenario for Observation-Hours
          info.isObservation() && !isDraft ? setCompleteResult(true) : void 0;
          return { isSaved: true, mode, status: nextSubmitStatus };
        } catch (error) {
          console.error(error);
          const isDeleted = error.status === 404;
          // custom scenario for Observation-Hours
          info.isObservation() && !isDraft ? setCompleteResult(false) : void 0;
          return { isSaved: false, mode, isDeleted };
        } finally {
          setSaving(false);
          setSavingMode(void 0);
        }
      },
      /**
       * This completes regular evaluation form (except observation hours)
       * @param {{reUseState?:boolean|null, isDeleted?:boolean, }} options
       */
      async complete(options = { reUseState: null }) {
        const { reUseState } = options ?? { reUseState: null };
        if (!recommendation) {
          return { isCompleted: false };
        }
        setCompleting(true);
        try {
          let path = `/recommendation/rest/completeRecommendation/${recommendation.recommendationRequest.id}`;
          if (isBoolean(reUseState)) {
            path = path + `?reuseForFutureEvals=${reUseState}`;
          }
          // await sendCompletedRecommendation(evaluationId, reUseState);
          // const path = evaluationId ? buildCompletedRecommendationPath(evaluationId, reUseState) : null;
          // uncomment this when you want to test the submissoin flow without actual submitting a request
          // return new Promise((r) => { setTimeout(r, 5000); });
          const response = DEV_MODE ? await Promise.resolve({ status: 200, statusText: 'OK' }) : await apiPOST(path);
          const isCompleted = response.status === 200;

          setCompleteResult(isCompleted);
          return { isCompleted };
        } catch (error) {
          console.error(error);
          const isDeleted = error.status === 404;
          setCompleteResult(false);
          return { isCompleted: false, isDeleted };
        } finally {
          setCompleting(false);
        }
      },
      // the shortcut to the evaluation.actions.decline(msg:string)
      async decline(/** @type {string} */ message) {
        return evaluationRequest.actions.decline(message);
      },
      dispose: () => {
        updateRecommendation(null, true);
      },
    },

    updateRecommendation,
    isEligibleForAutoFill: () => !!recommendation?.eligibleForAutoFill,
    getBlockByQuestionId: (/** @type {number | undefined} */ questionId) => {
      const blockId = questionIdsToBlockIdsMap?.[questionId ?? 0];
      return blockId ? getEvalationQuestionBlockById(recommendation, blockId) : void 0;
    },
    getEvalationQuestionById: (/** @type {number | undefined} */ questionId) =>
      getEvalationQuestionById(recommendation, questionId),
    getEvalationQuestionBlockById: (/** @type {number | undefined} */ blockId) =>
      getEvalationQuestionBlockById(recommendation, blockId),
  };
};

export default useRecommendation;

/**
 *
 * @param {number} questionBlockIdInput
 */
export const useQuestionBlock = questionBlockIdInput => {
  const { getEvalationQuestionBlockById, aliasVars } = useRecommendation();
  const item = getEvalationQuestionBlockById(questionBlockIdInput);

  return {
    item,
    isRatings: item?.constantBlockName === 'Rubrics',
    ratings: aliasVars.ratings,
  };
};

/**
 *
 * @param {number} questionIdInput
 */
export const useQuestionById = questionIdInput => {
  const recommendation = useRecommendation();
  const [questionId] = useState(questionIdInput);
  const [question, setQuestion] = useState(recommendation.getEvalationQuestionById(questionId));
  const questionRelatedBlock = recommendation.getBlockByQuestionId(questionId);
  const [activeRatingId, setActiveRatingId] = useState(/** @type {number=} */ (void 0));
  const localCopyRecommendationRef = useRef(recommendation.item);
  const [isDirty, setIsDirty] = useState(false);

  /*eslint no-unused-vars: "off" */
  // for the dubug purposes
  const dump = obj => {
    console.log(`${questionRelatedBlock?.constantBlockName} dump`, JSON.parse(JSON.stringify(obj)));
  };

  // refresh local copy
  useEffect(() => {
    setQuestion(recommendation.getEvalationQuestionById(questionId));
    localCopyRecommendationRef.current = recommendation.item;
  }, [recommendation?.item]);

  // when the block is ready - restore rating id in case this is a rating-question
  useEffect(() => {
    if (questionRelatedBlock?.constantBlockName === 'Rubrics') {
      const evaluationFormRubricsRatings = localCopyRecommendationRef.current?.evaluationFormRubricsRatings ?? [];
      const itemRating = evaluationFormRubricsRatings.find(
        rr => rr.ratingName === questionUserAnswerResolver(question?.userAnswer ?? [])?.toString()
      );
      setActiveRatingId(itemRating?.id);
    }
    // use the block's id only, rather than the whole block, to avoid constant invokation
  }, [questionRelatedBlock?.id]);

  /**
   * @typedef {import('../store/reducers/dashboardReducer').TEvaluationBlockQuestionUserAnswer} TEvaluationBlockQuestionUserAnswer;
   */

  /**
   * @param {null|TEvaluationBlockQuestionUserAnswer|TEvaluationBlockQuestionUserAnswer[]} params
   * @returns {TEvaluationBlockQuestionUserAnswer['answer']}
   */
  const questionUserAnswerResolver = params => {
    const answerObj = Array.isArray(params) ? params[0] : params;
    const result = answerObj?.answer ?? '';
    return result;
  };

  /**
   *
   * @param {?TEvaluationBlockQuestionUserAnswer=} v
   * @returns {{ toArray():TEvaluationBlockQuestionUserAnswer[]; raw():?TEvaluationBlockQuestionUserAnswer; }}
   */
  const questionUserAnswerObjectBuilder = v => {
    const result =
      !v || (typeof v.answer === 'undefined' && typeof v.comments === 'undefined')
        ? null
        : { answer: v.answer ?? null, comments: v.comments ?? null };

    return {
      toArray: () => (result ? [result] : []),
      raw: () => result,
    };
  };

  /**
   * @param {null|TEvaluationBlockQuestionUserAnswer|TEvaluationBlockQuestionUserAnswer[]} params
   * @returns {null|TEvaluationBlockQuestionUserAnswer}
   */
  const questionUserAnswerObjectResolver = params => {
    const answerObj = Array.isArray(params) ? params[0] : params;
    const result = answerObj;
    return result;
  };

  /**
   * @param {import('../store/reducers/dashboardReducer').TEvaluationDetails | null | undefined} nextRecommendationDetails
   */
  const updateRecommendation = nextRecommendationDetails => {
    localCopyRecommendationRef.current = nextRecommendationDetails;
    recommendation.updateRecommendation(nextRecommendationDetails);
  };

  /**
   * @param {import('../store/reducers/dashboardReducer').TEvaluationDetails | null | undefined} evaluationDetails
   * @param {?TEvaluationBlockQuestionUserAnswer[]} userAnswer
   * @returns {import('../store/reducers/dashboardReducer').TEvaluationDetails | null | undefined}
   */
  const buildNextRecommendationObject = (evaluationDetails, userAnswer) => {
    return evaluationDetails?.evaluationFormSection
      ? {
          ...evaluationDetails,
          evaluationFormSection: {
            ...evaluationDetails.evaluationFormSection,
            evaluationQuestionBlocks:
              evaluationDetails.evaluationFormSection.evaluationQuestionBlocks.map(qBlock => ({
                ...qBlock,
                evaluationQuestions: qBlock.evaluationQuestions.map(qItem => ({
                  ...qItem,
                  userAnswer: qItem.id === questionId ? userAnswer : qItem.userAnswer,
                  userAnswer_ui__initial: [...(qItem.userAnswer_ui__initial ?? qItem.userAnswer ?? [])],
                  userAnswer_ui__changed:
                    qItem.id !== questionId ||
                    (qItem.userAnswer_ui__initial ?? qItem.userAnswer ?? []).filter(v => !!v.answer || !!v.comments)
                      .length +
                      (userAnswer ?? []).filter(v => !!v.answer || !!v.comments).length ===
                      0
                      ? false
                      : !isEqual(qItem.userAnswer_ui__initial ?? qItem.userAnswer, userAnswer),
                })),
              })) ?? [],
          },
        }
      : void 0;
  };

  /**
   * @param {?TEvaluationBlockQuestionUserAnswer[]} userAnswer
   */
  const setUserAnswers = userAnswer => {
    const nextPayload = buildNextRecommendationObject(localCopyRecommendationRef.current, userAnswer);
    nextPayload ? updateRecommendation(nextPayload) : void 0;
  };

  /** @type {Record<string, import('../store/reducers/dashboardReducer').TEvaluationBlockQuestionItemDetails>} */
  const mapAnswerOptionToQuestionDetails =
    question?.evaluationQuestionDetails?.reduce(
      (r, qDeets) => ({
        ...r,
        [qDeets.answerOption ?? '']: qDeets,
      }),
      {}
    ) ?? {};

  return {
    question,
    questionRelatedBlock,
    ratings: {
      isDirty,
      count: recommendation.aliasVars.ratings.count,
      /** @param {number} ratingNumber */
      setRatingValue(ratingNumber) {
        const evaluationFormRubricsRatings = localCopyRecommendationRef.current?.evaluationFormRubricsRatings ?? [];
        const itemRating = evaluationFormRubricsRatings?.[ratingNumber];
        setActiveRatingId(itemRating?.id);
        // TODO: CHECK: save question answer
        const nextUserAnswer = itemRating?.ratingName
          ? questionUserAnswerObjectBuilder({
              ...questionUserAnswerObjectResolver(question?.userAnswer ?? []),
              answer: itemRating.ratingName,
            }).toArray()
          : [];
        setIsDirty(true);
        setTimeout(() => setUserAnswers(nextUserAnswer), 0);
      },
      /** @param {number} ratingNumber */
      getRatingItemAt(ratingNumber) {
        return localCopyRecommendationRef.current?.evaluationFormRubricsRatings[ratingNumber];
      },
      getRatingItem() {
        return localCopyRecommendationRef.current?.evaluationFormRubricsRatings.find(rr => rr.id === activeRatingId);
      },
      getRatingNumber() {
        return (
          localCopyRecommendationRef.current?.evaluationFormRubricsRatings.findIndex(rr => rr.id === activeRatingId) ??
          -1
        );
      },
      isEnabled() {
        return questionRelatedBlock?.constantBlockName === 'Rubrics';
      },
      isValid() {
        const currentAnswer = questionUserAnswerObjectResolver(question?.userAnswer ?? []);
        const qDetails = question?.evaluationQuestionDetails?.[0];
        const isValueSet = !!activeRatingId;
        // checking the value and comments if the last is required
        return isValueSet && (qDetails?.commentsRequired ? !!currentAnswer?.comments : true);
      },
      getStats() {
        const totalRatings = questionRelatedBlock?.evaluationQuestions.length ?? 0;
        const totalAnswers =
          questionRelatedBlock?.evaluationQuestions.filter(eQ => !!eQ.userAnswer?.[0]?.answer).length ?? 0;
        const totalNotObserved =
          questionRelatedBlock?.evaluationQuestions.filter(eQ => eQ.userAnswer?.[0]?.answer === 'Not Observed')
            .length ?? 0;
        return {
          totalRatings,
          totalAnswers,
          totalNotObserved,
          allSetToNotObserved: totalRatings === totalNotObserved,
          complete: totalRatings === totalAnswers - totalNotObserved,
        };
      },
    },
    mapAnswerOptionToQuestionDetails,
    getQuestionFirstDetails() {
      return question?.evaluationQuestionDetails?.[0];
    },
    /**
     * @param {string|null|undefined} answerOption
     * @returns {import('../store/reducers/dashboardReducer').TEvaluationBlockQuestionItemDetails=}
     */
    getQuestionDetailsByQuestionAnswer(answerOption) {
      return mapAnswerOptionToQuestionDetails[answerOption ?? ''];
    },
    /**
     *
     * @param {import('../store/reducers/dashboardReducer').TUploadedFile} uploadInfo
     */
    setEvaluationResponse(uploadInfo) {
      localCopyRecommendationRef.current
        ? updateRecommendation({
            ...localCopyRecommendationRef.current,
            evaluationResponse: {
              ...localCopyRecommendationRef.current?.evaluationResponse,
              id: uploadInfo.id,
              fileUploadedDate: uploadInfo.fileUploadedDate,
              fileName: uploadInfo.fileName,
              id_ui__initial: localCopyRecommendationRef.current?.evaluationResponse?.id_ui__initial ?? uploadInfo.id,
              id_ui__changed: true,
            },
          })
        : void 0;
    },
    removeEvaluationResponse() {
      localCopyRecommendationRef.current?.evaluationFormSection
        ? updateRecommendation({
            ...localCopyRecommendationRef.current,
            evaluationResponse: {
              ...(localCopyRecommendationRef.current?.evaluationResponse ?? {}),
              id: null,
              fileUploadedDate: null,
              fileName: null,
              id_ui__initial: localCopyRecommendationRef.current?.evaluationResponse?.id_ui__initial ?? null,
              id_ui__changed: (localCopyRecommendationRef.current?.evaluationResponse?.id_ui__initial ?? null) !== null,
            },
            // cleanup user answer of the related upload
            evaluationFormSection: {
              ...localCopyRecommendationRef.current.evaluationFormSection,
              evaluationQuestionBlocks:
                localCopyRecommendationRef.current.evaluationFormSection.evaluationQuestionBlocks.map(qBlock => ({
                  ...qBlock,
                  evaluationQuestions: qBlock.evaluationQuestions.map(qItem => ({
                    ...qItem,
                    userAnswer: qItem.id === questionId ? [] : qItem.userAnswer,
                  })),
                })) ?? [],
            },
          })
        : void 0;
    },
    getFirstUserAnswerObj() {
      return questionUserAnswerObjectResolver(question?.userAnswer ?? []);
    },
    getFirstUserAnswer() {
      return questionUserAnswerResolver(question?.userAnswer ?? []);
    },
    getFirstUserComment() {
      return questionUserAnswerObjectResolver(question?.userAnswer ?? [])?.comments;
    },
    /**
     *
     * @param {?(string|number|boolean)=} answerOption
     * @param {?string=} comments
     */
    setAnswerComment(answerOption, comments) {
      const nextUserAnswer =
        question?.userAnswer?.flatMap(u =>
          u.answer === answerOption
            ? questionUserAnswerObjectBuilder({
                ...u,
                comments,
              }).toArray()
            : [u]
        ) ?? [];
      setUserAnswers(nextUserAnswer);
    },
    /**
     *
     * @param {null|string|boolean|undefined} answer
     * @param {null|string|undefined} comments
     */
    addNewUserAnswer(answer, comments = null) {
      const nextUserAnswers = [
        ...(question?.userAnswer ?? []),
        ...questionUserAnswerObjectBuilder({ answer, comments }).toArray(),
      ];
      setUserAnswers(nextUserAnswers);
    },
    /**
     *
     * @param {null|string|boolean|undefined} answer
     */
    removeUserAnswer(answer) {
      const nextUserAnswers = (question?.userAnswer ?? [])
        .filter(u => u.answer !== answer)
        .flatMap(v => questionUserAnswerObjectBuilder(v).toArray());
      setUserAnswers(nextUserAnswers);
    },
    clearUserAnswer() {
      setUserAnswers([]);
    },
    /**
     *
     * @param {null|string|boolean|number|undefined} answer
     * @param {?string=} comments
     */
    setUserSingleAnswer(answer, comments) {
      const nextUserAnswer = questionUserAnswerObjectBuilder({
        ...questionUserAnswerObjectResolver(question?.userAnswer ?? []),
        answer,
        ...(typeof comments === 'undefined' ? {} : { comments }),
      }).toArray();
      setUserAnswers(nextUserAnswer);
    },
    /**
     *
     * @param {null|string|undefined} comments
     */
    setUserSingleComment(comments) {
      const nextUserAnswer = questionUserAnswerObjectBuilder({
        ...questionUserAnswerObjectResolver(question?.userAnswer ?? []),
        comments,
      }).toArray();
      setUserAnswers(nextUserAnswer);
    },
  };
};
